import type { SupportedLocale } from "staticConfiguration";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import { useRichTreeViewApiRef } from "@mui/x-tree-view";
import type { RemoteServiceErrorResponse } from "core/entities/CentrixTypes";
import { useAvailableLocaleOptions } from "core/hooks/useAvailableLocaleOptions";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useLocale } from "core/hooks/useLocale";
import { useRevalidatePaths } from "core/hooks/useRevalidatePaths";
import { useToaster } from "core/hooks/useToaster";
import { sortBasedOnReferenceOrder } from "core/utils/sortBasedOnReferenceOrder";
import type { DocNodeBasicSummaryDto } from "features/DMS/entities";
import { buildDocNodeFileTreeFromSummaryDto } from "features/DMS/utils/buildDocNodeFileTreeFromSummaryDto";
import { getAllParentPaths } from "features/DMS/utils/getAllParentPaths";
import { useMemo, useState } from "react";
import { useController, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ApiErrorDisplayAccordion } from "../ApiErrorDisplayAccordion";
import { ComponentSwitcher } from "../ComponentSwitcher";
import { DialogFormContainer } from "../DialogFormContainer";
import { ReactHookSwitch } from "../ReactHookFormComponents/ReactHookSwitch";
import { StyledTabs } from "../StyledTabs";
import { isCustomLinkValid } from "./isCustomLinkValid";
import { mapQuickLinkFormDataToCreateUpdateDto } from "./mapQuickLinkFormDataToCreateUpdateDto";
import { mapQuickLinkWidgetSettingsToFormValues } from "./mapQuickLinkWidgetSettingsToFormValues";
import { QuickLinkCustomLinkForm } from "./QuickLinkCustomLinkForm";
import {
  MAXIMUM_QUICK_LINKS,
  type QuickLinkWidgetFormValues,
} from "./QuickLinkDefs";
import { QuickLinkDocNodeForm } from "./QuickLinkDocNodeForm";
import {
  type OrderedEntry,
  SelectedQuickLinksList,
} from "./SelectedQuickLinksList";
import { useQuickLinksWidgetSettings } from "./useQuickLinksWidgetSettings";

const defaultValues: QuickLinkWidgetFormValues = {
  isEnabled: true,
  currentTab: "DocNode",
  selectedQuickLinks: [],
  quickLinkOrder: [],
};

export function QuickLinksWidgetSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:QuickLinksWidgetSettingsDialog",
  });
  const revalidatePaths = useRevalidatePaths();
  const locale = useLocale();
  const brcyCode = useBrcyCode();
  const {
    data: nodeData,
    isError: nodeDataError,
    isLoading: nodeDataLoading,
  } = useCentrixApi({
    path: "/api/app/doc-node-manage/user-accessible-nodes-for-country-brand",
    shouldFetch: open,
    parameters: {
      query: {
        brcyCode,
        locale,
      },
    },
  });
  const {
    data: widgetSettingData,
    isLoading: widgetSettingLoading,
    isError: widgetSettingError,
  } = useQuickLinksWidgetSettings();
  const treeItems = useMemo(() => {
    if (nodeDataLoading || !nodeData || !Array.isArray(nodeData.items)) {
      return [];
    }
    return buildDocNodeFileTreeFromSummaryDto(nodeData.items);
  }, [nodeData, nodeDataLoading]);

  const maxSelections = widgetSettingData?.maxLinkSlots ?? MAXIMUM_QUICK_LINKS;

  const centrixFetch = useCentrixFetch();
  const { localeOptions } = useAvailableLocaleOptions();
  const values = useMemo(() => {
    if (!widgetSettingData) {
      return undefined;
    }
    return mapQuickLinkWidgetSettingsToFormValues(
      widgetSettingData,
      locale as SupportedLocale
    );
  }, [widgetSettingData, locale]);

  const formMethods = useForm<QuickLinkWidgetFormValues>({
    defaultValues,
    values: values ?? defaultValues,
  });
  useController({
    control: formMethods.control,
    name: "selectedQuickLinks",
    rules: {
      validate: (selectedQuickLinks) => {
        if (selectedQuickLinks.length > maxSelections) {
          return t("validation.maxLinks", {
            defaultValue: "You can select up to {{max}} quick links.",
            max: maxSelections,
          });
        }
        const hasInvalidCustomLinks = selectedQuickLinks.some(
          (ql) => ql.type === "CustomLink" && !isCustomLinkValid(ql.customLink)
        );
        if (hasInvalidCustomLinks) {
          return t(
            "validation.incompleteCustomLinks",
            "All custom link translations must have a name and URL."
          );
        }
      },
    },
  });

  const [activeTab, setActiveTab] = useState<"DocNode" | "CustomLink">(
    "DocNode"
  );

  const apiRef = useRichTreeViewApiRef();

  const fieldArray = useFieldArray({
    control: formMethods.control,
    name: "selectedQuickLinks",
  });

  const allSelectedQuickLinks = formMethods.watch("selectedQuickLinks");
  const selectedQuickLinkIds = allSelectedQuickLinks.map((ql) => ql.id);
  const selectedDocNodeIds = allSelectedQuickLinks
    .filter((ql) => ql.type === "DocNode")
    .map((ql) => ql.id);
  const quickLinkOrder = formMethods.watch("quickLinkOrder");
  const orderedSelectedIds = sortBasedOnReferenceOrder(
    selectedQuickLinkIds,
    quickLinkOrder
  );
  const nodeMap = useMemo(() => {
    if (!nodeData || !Array.isArray(nodeData.items)) {
      return new Map<string, DocNodeBasicSummaryDto>();
    }
    const map = new Map<string, DocNodeBasicSummaryDto>(
      nodeData.items.map((item) => [item.path!, item])
    );
    return map;
  }, [nodeData]);

  const quickLinkMap = useMemo(
    () => new Map(allSelectedQuickLinks.map((ql) => [ql.id, ql])),
    [allSelectedQuickLinks]
  );

  const orderedEntries = orderedSelectedIds.reduce((acc, id) => {
    const ql = quickLinkMap.get(id);
    if (!ql) {
      return acc;
    }

    if (ql.type === "DocNode") {
      const currentNode = nodeMap.get(id);
      if (!currentNode) {
        return acc;
      }
      const parentPaths = getAllParentPaths(id);
      const parentNodes: DocNodeBasicSummaryDto[] = [];
      for (const parentPath of parentPaths) {
        const parentNode = nodeMap.get(parentPath);
        if (parentNode) {
          parentNodes.push(parentNode);
        }
      }
      acc.push({ type: "DocNode", node: currentNode, parents: parentNodes });
    } else if (ql.type === "CustomLink") {
      acc.push({ type: "CustomLink", quickLink: ql });
    }
    return acc;
  }, [] as OrderedEntry[]);

  const [error, setError] = useState<RemoteServiceErrorResponse | null>(null);
  const { isSubmitting } = formMethods.formState;
  const { successToast, errorToast } = useToaster();

  const successMessage = t(
    "successToast",
    "Quick links widget settings saved successfully."
  );
  const errorMessage = t(
    "errorToast",
    "An error occurred while saving quick links widget settings."
  );

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    setError(null);
    const treeItemToDocNodeId = (id: string) => nodeMap.get(id)?.id;
    const body = mapQuickLinkFormDataToCreateUpdateDto(
      data,
      brcyCode,
      treeItemToDocNodeId
    );
    const res = await centrixFetch({
      path: "/api/app/quick-link/settings",
      method: "put",
      body,
    });
    if (!res.ok) {
      const errorData = await res.json();
      setError(errorData);
      errorToast(errorMessage);
      return;
    }
    await res.json(); // run request to completion
    await revalidatePaths("quick-link", true);
    successToast(successMessage);
    onClose();
  });

  return (
    <DialogFormContainer
      onSubmit={handleSubmit}
      maxWidth="xl"
      open={open}
      onDialogClose={onClose}
      title={t("dialogTitle", "Quick Links Widget Settings")}
      onCancelButtonClick={onClose}
      isSubmitting={isSubmitting}
      onResetButtonClick={() => {
        setError(null);
        formMethods.reset(values);
      }}
      isError={widgetSettingError || nodeDataError}
      isLoading={widgetSettingLoading || nodeDataLoading}
      loadingSlot={<Skeleton variant="rounded" width="100%" height="60vh" />}
    >
      <FormHelperText>
        <HelpOutlineIcon
          fontSize="small"
          color="inherit"
          sx={{ verticalAlign: "middle", mr: 1 }}
        />
        {t(
          "dmsWarning",
          `Note: this form displays the documents you have permission to view and/or manage. 
           The widget will only display the documents that you have permission to view.
           `
        )}
      </FormHelperText>
      <Divider sx={{ my: 2 }} />
      <ReactHookSwitch
        control={formMethods.control}
        name="isEnabled"
        label={t("enableWidget", "Enable Quick Links Widget")}
      />
      <Collapse in={formMethods.watch("isEnabled")}>
        <Box
          component={Paper}
          variant="outlined"
          p={2}
          display="grid"
          gridTemplateColumns="1fr auto 2fr"
          gap={4}
        >
          <Box>
            <Box>
              <StyledTabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
              >
                <Tab label={t("tabDocuments", "Documents")} value="DocNode" />
                <Tab label={t("tabLinks", "Links")} value="CustomLink" />
              </StyledTabs>
              <Divider sx={{ mb: 2 }} />
            </Box>
            <ComponentSwitcher
              switchPosition={activeTab === "DocNode"}
              ComponentOne={
                <QuickLinkDocNodeForm
                  apiRef={apiRef}
                  treeItems={treeItems}
                  selectedDocNodeIds={selectedDocNodeIds}
                  allSelectedQuickLinks={allSelectedQuickLinks}
                  maxSelections={maxSelections}
                  fieldArray={fieldArray}
                  t={t}
                />
              }
              ComponentTwo={
                <QuickLinkCustomLinkForm
                  formMethods={formMethods}
                  fieldArray={fieldArray}
                  locale={locale}
                  localeOptions={localeOptions}
                  t={t}
                  maxSelections={maxSelections}
                />
              }
            />
          </Box>
          <Divider orientation="vertical" flexItem />
          <SelectedQuickLinksList
            orderedEntries={orderedEntries}
            selectedQuickLinkIds={selectedQuickLinkIds}
            formMethods={formMethods}
            locale={locale}
            maxSelections={maxSelections}
          />
        </Box>
      </Collapse>
      <ApiErrorDisplayAccordion errorResponse={error} />
    </DialogFormContainer>
  );
}
